import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Transaction } from '../../types/Transaction';

export type RecurringScope = 'instance' | 'future' | 'all';

export interface RecurringScopeDialogProps {
  open: boolean;
  mode: 'edit' | 'delete' | 'reschedule';
  transaction: Transaction | null;
  onClose: () => void;
  onSelect: (scope: RecurringScope) => void;
}

const formatAmount = (value?: number): string =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value ?? 0);

const RecurringScopeDialog: React.FC<RecurringScopeDialogProps> = ({
  open,
  mode,
  transaction,
  onClose,
  onSelect,
}) => {
  const hasTransaction = Boolean(transaction);
  const title =
    mode === 'edit'
      ? 'Edit recurring transaction'
      : mode === 'delete'
        ? 'Delete recurring transaction'
        : 'Reschedule recurring transaction';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ 'data-testid': 'recurring-scope-dialog' }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 1.5 }}>
          Choose scope for this recurring action.
        </Typography>
        <Typography variant="body2">This instance affects only the selected date.</Typography>
        <Typography variant="body2">This and future affects items on/after the selected transaction date.</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          All in series affects all instances currently in the series.
        </Typography>

        {transaction ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {transaction.date} • {transaction.description || '(No description)'} • {formatAmount(transaction.amount)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No transaction selected
          </Typography>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            onClick={() => onSelect('instance')}
            disabled={!hasTransaction}
            data-testid="recurring-scope-instance"
          >
            This instance
          </Button>
          <Button
            variant="contained"
            onClick={() => onSelect('future')}
            disabled={!hasTransaction}
            data-testid="recurring-scope-future"
          >
            This and future
          </Button>
          <Button
            variant="contained"
            onClick={() => onSelect('all')}
            disabled={!hasTransaction}
            data-testid="recurring-scope-all"
          >
            All in series
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" data-testid="recurring-scope-cancel">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurringScopeDialog;
